import React from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';

// Renders lightweight markdown (## / ### headings, lists, **bold**, [links],
// images, blockquotes, paragraphs) as real HTML tags. Using proper heading tags
// (h2/h3) structures the copy for SEO instead of one flat paragraph. Content is
// admin-authored (trusted); no raw HTML is injected.
export default function RichText({ content, className = 'prose prose-gray max-w-none' }) {
  const { formatText } = useCurrency();
  if (!content) return null;

  const lines = formatText(content).split('\n');
  const elements = [];
  let inList = false;
  let listItems = [];

  const parseInline = (text) => {
    const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link) {
        return link[2].startsWith('/')
          ? <Link key={i} to={link[2]} className="font-semibold text-[#1fa5a3] underline decoration-[#1fa5a3]/40 hover:text-[#c8a25a]">{link[1]}</Link>
          : <a key={i} href={link[2]} target="_blank" rel="noreferrer" className="font-semibold text-[#1fa5a3] underline hover:text-[#c8a25a]">{link[1]}</a>;
      }
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
      return <span key={i}>{part}</span>;
    });
  };

  const flushList = (key) => {
    if (inList) {
      elements.push(<ul key={`list-${key}`} className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-6 space-y-2">{listItems}</ul>);
      listItems = [];
      inList = false;
    }
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (trimmed === '---') { flushList(index); elements.push(<hr key={index} className="my-8 border-gray-200" />); return; }

    if (trimmed.startsWith('## ')) {
      flushList(index);
      elements.push(<h2 key={index} className="text-2xl md:text-3xl font-bold text-[#14213d] dark:text-white mt-8 mb-3">{trimmed.replace('## ', '')}</h2>);
      return;
    }
    if (trimmed.startsWith('### ')) {
      flushList(index);
      elements.push(<h3 key={index} className="text-lg font-semibold text-[#14213d] dark:text-white mt-6 mb-2">{trimmed.replace('### ', '')}</h3>);
      return;
    }

    if (trimmed.startsWith('> ')) {
      flushList(index);
      elements.push(
        <blockquote key={index} className="border-l-4 border-[#c8a25a] pl-5 italic text-gray-600 dark:text-gray-300 my-5">
          <p className="not-italic">{parseInline(trimmed.replace('> ', ''))}</p>
        </blockquote>
      );
      return;
    }

    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      inList = true;
      listItems.push(<li key={index} className="leading-relaxed">{parseInline(trimmed.replace(/^[-*]\s*/, ''))}</li>);
      return;
    }
    flushList(index);

    if (trimmed) {
      elements.push(<p key={index} className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">{parseInline(trimmed)}</p>);
    }
  });
  flushList('end');

  return <div className={className}>{elements}</div>;
}
